import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface ExpenseBillsContextType {
  bills: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  error: string | null;
  fetchExpenseBills: (filters?: {
    project_id?: number;
    page?: number;
    per_page?: number;
    date_from?: string;
    date_to?: string;
  }) => Promise<void>;
  addExpenseBill: (bill: any) => Promise<void>;
  editExpenseBill: (bill: any) => Promise<void>;
  deleteExpenseBill: (billId: number | string) => Promise<void>;
}

export const ExpenseBillsContext = createContext<
  ExpenseBillsContextType | undefined
>(undefined);

interface ExpenseBillsProviderProps {
  children: ReactNode;
}

export const useExpenseBillsContext = (): ExpenseBillsContextType => {
  const context = useContext(ExpenseBillsContext);
  if (!context) {
    throw new Error(
      "useExpenseBillsContext must be used within ExpenseBillsProvider",
    );
  }
  return context;
};

const ExpenseBillsProvider: React.FC<ExpenseBillsProviderProps> = ({
  children,
}) => {
  const [bills, setBills] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all expense bills with pagination
  const fetchExpenseBills = useCallback(async (filters?: { project_id?: number; page?: number; per_page?: number; date_from?: string; date_to?: string }) => {
    setLoading(true);
    setError(null);
    try {
      let url = "/expense-bills";
      const params = new URLSearchParams();
      if (filters?.project_id) params.append('project_id', filters.project_id.toString());
      if (filters?.date_from) params.append('date_from', filters.date_from);
      if (filters?.date_to) params.append('date_to', filters.date_to);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.per_page) params.append('per_page', filters.per_page.toString());
      if (params.toString()) url += `?${params.toString()}`;
      const response = await api.get(url);
      
      // Handle Laravel pagination structure: { success: true, data: { data: [...], current_page, last_page, per_page, total, ... } }
      let billsData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          // Laravel pagination structure: pagination metadata is directly in response.data
          billsData = responseData.data.data;
          
          // Extract pagination from Laravel pagination structure (current_page, last_page, per_page, total are at data level)
          if (responseData.data.current_page !== undefined) {
            paginationData = {
              current_page: responseData.data.current_page,
              last_page: responseData.data.last_page,
              per_page: responseData.data.per_page,
              total: responseData.data.total,
            };
          } else if (responseData.data.pagination) {
            // Fallback: check if pagination is nested
            paginationData = responseData.data.pagination;
          } else if (responseData.pagination) {
            paginationData = responseData.pagination;
          }
        } else if (responseData.data && Array.isArray(responseData.data)) {
          // Check if response.data itself is an array or has pagination
          if (typeof responseData.data === 'object' && 'current_page' in responseData.data) {
            // Laravel pagination: response.data has current_page, data, etc.
            billsData = Array.isArray(responseData.data.data) ? responseData.data.data : [];
            paginationData = {
              current_page: responseData.data.current_page,
              last_page: responseData.data.last_page,
              per_page: responseData.data.per_page,
              total: responseData.data.total,
            };
          } else {
            billsData = responseData.data;
            paginationData = responseData.pagination;
          }
        } else if (Array.isArray(responseData)) {
          billsData = responseData;
        }
      } else if (responseData?.data) {
        // Handle case where success might not be present
        if (responseData.data.data && Array.isArray(responseData.data.data)) {
          billsData = responseData.data.data;
          // Check for Laravel pagination structure
          if (responseData.data.current_page !== undefined) {
            paginationData = {
              current_page: responseData.data.current_page,
              last_page: responseData.data.last_page,
              per_page: responseData.data.per_page,
              total: responseData.data.total,
            };
          } else if (responseData.data.pagination) {
            paginationData = responseData.data.pagination;
          }
        } else if (Array.isArray(responseData.data)) {
          billsData = responseData.data;
        }
      } else if (Array.isArray(responseData)) {
        billsData = responseData;
      }
      
      setBills(billsData);
      setPagination(paginationData);
    } catch (err) {
      setError("Failed to fetch expense bills.");
      console.error(err);
      setBills([]); // Set empty array on error
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new expense bill
  const addExpenseBill = async (bill: any): Promise<void> => {
    setError(null);
    try {
      const formData = createFormData(bill);
      const response = await api.post("/expense-bills", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newBill = response.data?.data || response.data;
      setBills((prevBills) => [...prevBills, newBill]);
    } catch (err) {
      setError("Failed to add expense bill.");
      console.error(err);
      throw err;
    }
  };

  // Edit an expense bill
  const editExpenseBill = async (bill: any): Promise<void> => {
    setError(null);
    try {
      const formData = createFormData(bill);
      const billId = bill.id || bill.billId || bill.bill_id;
      formData.append("_method", "PUT");
      const response = await api.post(`/expense-bills/${billId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBills((prevBills) =>
        prevBills.map((b) => {
          const currentId = b.id || b.billId || b.bill_id;
          return currentId === billId ? (response.data?.data || response.data) : b;
        })
      );
    } catch (err) {
      setError("Failed to edit expense bill.");
      console.error(err);
      throw err;
    }
  };

  // Delete an expense bill
  const deleteExpenseBill = async (
    billId: number | string,
  ): Promise<void> => {
    setError(null);
    try {
      await api.delete(`/expense-bills/${billId}`);
      setBills((prevBills) => prevBills.filter((b) => {
        const currentId = b.id || b.billId || b.bill_id;
        return currentId !== billId;
      }));
    } catch (err) {
      setError("Failed to delete expense bill.");
      console.error(err);
      throw err;
    }
  };

  // Helper function to create FormData
  const createFormData = (bill: any) => {
    const formData = new FormData();
    const toFormValue = (value: unknown): string | Blob => {
      if (value instanceof Blob) {
        return value;
      }
      return String(value ?? "");
    };
    const getSelectedFile = (value: unknown): File | null => {
      if (typeof FileList !== "undefined" && value instanceof FileList) {
        return value.item(0);
      }

      if (value instanceof File) {
        return value;
      }

      return null;
    };

    // Append regular fields
    Object.entries(bill).forEach(([key, value]) => {
      if (key === "transactionDetails") {
        // Append nested transactionDetails
        Object.entries(value as Record<string, any>).forEach(
          ([nestedKey, nestedValue]) => {
            formData.append(`transactionDetails[${nestedKey}]`, toFormValue(nestedValue));
          },
        );
      } else if (key === "uploadBillCopy") {
        const file = getSelectedFile(value);
        if (file) {
          formData.append(key, file);
        }
      } else {
        formData.append(key, toFormValue(value));
      }
    });

    return formData;
  };

  // Remove auto-fetch on mount to prevent infinite loops
  // Data will be fetched when explicitly called from components

  return (
    <ExpenseBillsContext.Provider
      value={{
        bills,
        pagination,
        loading,
        error,
        fetchExpenseBills,
        addExpenseBill,
        editExpenseBill,
        deleteExpenseBill,
      }}
    >
      {children}
    </ExpenseBillsContext.Provider>
  );
};

export default ExpenseBillsProvider;
